import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageCircle, ArrowLeft } from 'lucide-react';
import { CartItem } from '../components/CartItem';
import { useCart } from '../hooks/useCart';
import { useCreateOrder } from '../hooks/useQueries';
import { api } from '../lib/api';

export function CartPage() {
  const navigate = useNavigate();
  const { cart, updateQuantity, getTotalPrice } = useCart();
  const [showCheckout, setShowCheckout] = useState(false);
  const [customerData, setCustomerData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleCheckout = async () => {
    if (!customerData.name || !customerData.phone) {
      alert('Please fill in your name and phone number');
      return;
    }

    setIsSubmitting(true);
    try {
      const order = await api.createOrder(customerData);
      
      // Create WhatsApp message
      const items = cart.map(item => 
        `• ${item.product.name} x${item.quantity} - ${formatPrice(item.product.price * item.quantity)}`
      ).join('\n');
      
      const message = `🛒 *New Order - #${order.id.slice(-6)}*

📋 *Order Details:*
${items}

💰 *Total: ${formatPrice(order.total)}*

👤 *Customer Info:*
Name: ${customerData.name}
${customerData.email ? `Email: ${customerData.email}` : ''}
Phone: ${customerData.phone}

Thank you for your order! 🙏`;

      const whatsappUrl = `https://wa.me/6281234567890?text=${encodeURIComponent(message)}`;
      window.open(whatsappUrl, '_blank');
      
      navigate(-1);
    } catch (error) {
      console.error('Error creating order:', error);
      alert('Failed to create order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-md mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back
              </button>
              <h1 className="text-xl font-semibold text-gray-900">Shopping Cart</h1>
              <div className="w-16" />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <div className="text-6xl mb-4">🛒</div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Your cart is empty
            </h3>
            <p className="text-gray-600 mb-6">
              Add some products to get started!
            </p>
            <button
              onClick={() => navigate(-1)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-semibold transition duration-300"
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </button>
            <h1 className="text-xl font-semibold text-gray-900">Shopping Cart</h1>
            <div className="w-16" />
          </div>
        </div>
      </div>
      <div className="max-w-md mx-auto p-4 space-y-4">
        {/* Cart Items */}
        <div className="space-y-3">
          {cart.map(item => (
            <CartItem
              key={item.id}
              item={item}
              onUpdateQuantity={updateQuantity}
            />
          ))}
        </div>

        {/* Total */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-xl">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">Total</span>
            <span className="text-2xl font-bold">
              {formatPrice(getTotalPrice())}
            </span>
          </div>
        </div>

        {/* Checkout Section */}
        {!showCheckout ? (
          <button
            onClick={() => setShowCheckout(true)}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg transform transition-all duration-300 hover:scale-[1.02]"
          >
            Proceed to Checkout
          </button>
        ) : (
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Contact Information
            </h3>
            
            <div className="space-y-3">
              <input
                type="text"
                placeholder="Full Name *"
                value={customerData.name}
                onChange={(e) => setCustomerData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <input
                type="email"
                placeholder="Email (optional)"
                value={customerData.email}
                onChange={(e) => setCustomerData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <input
                type="tel"
                placeholder="WhatsApp Number *"
                value={customerData.phone}
                onChange={(e) => setCustomerData(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <button
              onClick={handleCheckout}
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 disabled:from-gray-400 disabled:to-gray-500 text-white py-4 px-6 rounded-xl font-semibold text-lg shadow-lg transform transition-all duration-300 hover:scale-[1.02] disabled:transform-none"
            >
              <div className="flex items-center justify-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <span>
                  {isSubmitting ? 'Processing...' : 'Order via WhatsApp'}
                </span>
              </div>
            </button>

            <button
              onClick={() => setShowCheckout(false)}
              className="w-full text-gray-600 py-3 font-medium hover:text-gray-900 transition-colors"
            >
              Back to Cart
            </button>
          </div>
        )}
      </div>
    </div>
  );
}